// src/pages/Register.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faAt,
  faLock,
  faSignature,
  faPhone,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import ReCAPTCHA from "react-google-recaptcha";

// Base URL for your backend API - Ensure this is correct
const API_URL = process.env.REACT_APP_API_URL;
// --- IMPORTANT: Replace with your actual Google reCAPTCHA Site Key ---
const RECAPTCHA_SITE_KEY = "6Le7kCUrAAAAAA3tFF5vrS82Dx0NfLcGJKbAEgXq";

const Register = () => {
  useEffect(() => {
    document.title = "Register - beaconSMS";
  }, []);

  const navigate = useNavigate();
  const recaptchaRef = useRef(null); // Ref for interacting with the ReCAPTCHA component

  // State to hold all form field values
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    dob: "", // Date of Birth
    password: "",
    confirmPassword: "",
  });

  // State for managing UI feedback (loading indicator, messages)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null); // State for the ReCAPTCHA response token

  // Calculate today's date in YYYY-MM-DD format for the date input's max attribute
  const today = new Date().toISOString().split("T")[0];

  // Generic handler for updating form field state
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear messages when user starts typing in any field
    setError(null);
    setSuccessMessage(null);
  };

  // Handler for when the ReCAPTCHA value changes (user solves it)
  const onCaptchaChange = (token) => {
    console.log("Captcha value obtained:", !!token); // Log if token is present
    setCaptchaToken(token); // Store the token
    setError(null); // Clear any previous errors (like "Please complete CAPTCHA")
  };

  // Helper Function to Calculate Age from YYYY-MM-DD string
  const calculateAge = (dateString) => {
    if (!dateString) return 0;
    try {
      // Ensure the date string is valid before creating Date objects
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return 0;

      const birthDate = new Date(dateString);
      // Double check if date is valid after parsing
      if (isNaN(birthDate.getTime())) return 0;

      const todayDate = new Date();
      let age = todayDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = todayDate.getMonth() - birthDate.getMonth();
      // Adjust age if birthday hasn't occurred yet this year
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && todayDate.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    } catch (e) {
      console.error("Error calculating age:", e);
      return 0; // Return 0 on error
    }
  };

  // Handler for the form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default HTML form submission
    setError(null); // Clear previous errors
    setSuccessMessage(null); // Clear previous success messages

    // --- Client-Side Validations ---
    if (!captchaToken) {
      setError("Please complete the CAPTCHA verification.");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (!formData.dob) {
      setError("Please select your Date of Birth.");
      return;
    }
    // Age Validation
    const age = calculateAge(formData.dob);
    if (age < 16) {
      setError("You must be at least 16 years old to register.");
      return;
    }
    // Add other client-side checks if needed (e.g., basic phone/email format)
    // const phoneRegex = /^\d{10,15}$/;
    // if (!phoneRegex.test(formData.phone)) { setError("Invalid phone number format."); return; }
    // --- End Validations ---

    setLoading(true); // Show loading indicator

    try {
      // Prepare data for the backend (exclude confirmPassword)
      const { confirmPassword, ...dataToSend } = formData;
      const registerUrl = `${API_URL}/register`; // Backend registration endpoint
      const payload = {
        ...dataToSend, // Includes name, username, email, phone, dob, password
        recaptchaToken: captchaToken, // Add the captcha token
      };

      console.log("Submitting registration data:", payload);
      const response = await axios.post(registerUrl, payload); // Send POST request

      // --- Handle Successful Registration ---
      console.log("Registration successful response:", response.data);
      setSuccessMessage(
        response.data?.message ||
          "Registration successful! Redirecting to login..."
      );
      // Clear the form fields
      setFormData({
        name: "",
        username: "",
        email: "",
        phone: "",
        dob: "",
        password: "",
        confirmPassword: "",
      });
      setCaptchaToken(null); // Reset captcha token state
      if (recaptchaRef.current) {
        recaptchaRef.current.reset(); // Visually reset the ReCAPTCHA widget
      }
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 2500);
    } catch (err) {
      // --- Handle Errors During Submission ---
      console.error("Registration failed:", err);
      let errorMessage = "Registration failed. Please try again later."; // Default error
      // Extract specific error message from backend response if available
      if (axios.isAxiosError(err)) {
        if (err.response) {
          errorMessage =
            err.response.data?.message ||
            err.response.data?.error ||
            `Server error (${err.response.status})`;
          // You could add more specific messages based on status code here
          // if (err.response.status === 409) { ... }
          // if (err.response.status === 400) { ... }
        } else if (err.request) {
          errorMessage =
            "Cannot reach server. Please check your network connection.";
        }
      } else {
        // Handle non-Axios errors
        errorMessage = err.message || "An unknown error occurred.";
      }
      setError(errorMessage); // Display the error message to the user
      // Reset CAPTCHA on error so user can retry
      setCaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      // Always stop the loading indicator, regardless of success or error
      setLoading(false);
    }
  };

  // --- Render the Registration Form ---
  return (
    <div className='bg-primary bg-gradient min-vh-100 d-flex flex-column'>
      <Container className='my-auto'>
        <Row
          className='justify-content-center align-items-center'
          style={{ padding: "10px" }}
        >
          <Col md={7} lg={6} xl={5}>
            <h4 className='text-center text-white mt-4 mb-3'>
              beaconSMS Registration
            </h4>
            <Card className='mt-2 shadow-sm'>
              <Card.Body className='p-4'>
                {/* Area to Display Success or Error Messages */}
                <div
                  style={{ minHeight: "60px" }}
                  className='d-flex align-items-center justify-content-center mb-3'
                >
                  {error && (
                    <Alert
                      variant='danger'
                      className='text-center w-100 m-0 py-2'
                    >
                      {error}
                    </Alert>
                  )}
                  {successMessage && (
                    <Alert
                      variant='success'
                      className='text-center w-100 m-0 py-2'
                    >
                      {successMessage}
                    </Alert>
                  )}
                </div>

                {/* Registration Form */}
                <Form onSubmit={handleSubmit}>
                  {/* Full Name */}
                  <Form.Group className='mb-3' controlId='register-name'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faSignature}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='text'
                        placeholder='Full Name'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Username */}
                  <Form.Group className='mb-3' controlId='register-username'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faUser}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='text'
                        placeholder='Username'
                        name='username'
                        value={formData.username}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Email */}
                  <Form.Group className='mb-3' controlId='register-email'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faAt}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='email'
                        placeholder='Email Address'
                        name='email'
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Phone Number */}
                  <Form.Group className='mb-3' controlId='register-phone'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faPhone}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='tel'
                        placeholder='Phone Number (e.g., 08012345678)'
                        name='phone'
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Date of Birth */}
                  <Form.Group className='mb-3' controlId='register-dob'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='date'
                        name='dob'
                        value={formData.dob}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        max={today}
                      />
                    </InputGroup>
                    <Form.Text muted> Date of Birth </Form.Text>
                  </Form.Group>

                  {/* Password */}
                  <Form.Group className='mb-3' controlId='register-password'>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faLock}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='password'
                        placeholder='Password (min 6 chars)'
                        name='password'
                        value={formData.password}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Confirm Password */}
                  <Form.Group
                    className='mb-3'
                    controlId='register-confirmPassword'
                  >
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon
                          icon={faLock}
                          className='text-primary fa-fw'
                        />
                      </InputGroup.Text>
                      <Form.Control
                        type='password'
                        placeholder='Confirm Password'
                        name='confirmPassword'
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </InputGroup>
                  </Form.Group>

                  {/* Google ReCAPTCHA */}
                  <Form.Group className='mb-4 d-flex justify-content-center'>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY} // Use your site key
                      onChange={onCaptchaChange}
                      onExpired={() => setCaptchaToken(null)} // Reset token if it expires
                      onError={() => {
                        // Handle errors loading/interacting with captcha
                        setError("CAPTCHA error. Please refresh the page.");
                        setCaptchaToken(null);
                      }}
                    />
                  </Form.Group>

                  {/* Submit Button */}
                  <div className='d-grid mt-4'>
                    <Button
                      variant='primary'
                      type='submit'
                      disabled={loading || !captchaToken}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                          />{" "}
                          Registering...
                        </>
                      ) : (
                        "Register"
                      )}
                    </Button>
                  </div>
                </Form>

                {/* Link to Login Page */}
                <div className='text-center mt-4'>
                  <span className='text-muted'>Already have an account? </span>
                  <Link
                    to='/login'
                    className='text-primary text-decoration-none'
                  >
                    Login Here
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      {/* Optional Footer */}
      {/* <Footer /> */}
    </div>
  );
};

export default Register;
